type NamedConcept = {
  name?: string;
  key?: string;
};

export function humanizeDisplayName(value: string): string {
  const text = value.trim();
  if (!text) {
    return text;
  }
  return text
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function slugifyDisplayKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function looksTechnicalIdentifier(value: string): boolean {
  const text = value.trim();
  if (!text) {
    return false;
  }
  if (text.includes("_") || text.includes("-")) {
    return true;
  }
  return slugifyDisplayKey(text) === text && text.includes("_");
}

function looksLikeMachineKey(value: string): boolean {
  const text = value.trim();
  if (!text) {
    return false;
  }
  if (looksTechnicalIdentifier(text)) {
    return true;
  }
  return text === text.toLowerCase() && !text.includes(" ");
}

export function resolveDisplayName(
  name: string | undefined,
  key?: string,
): string {
  const trimmedName = (name ?? "").trim();
  const trimmedKey = (key ?? "").trim();
  if (trimmedName) {
    if (trimmedKey && trimmedName === trimmedKey && looksLikeMachineKey(trimmedName)) {
      return humanizeDisplayName(trimmedName);
    }
    if (looksTechnicalIdentifier(trimmedName)) {
      return humanizeDisplayName(trimmedName);
    }
    return trimmedName;
  }
  if (trimmedKey) {
    return humanizeDisplayName(trimmedKey);
  }
  return "";
}

export function resolveEntityLabel(
  entityKey: string,
  entities: NamedConcept[] = [],
): string {
  const key = entityKey.trim();
  if (!key) {
    return "—";
  }
  const entity = entities.find((entry) => (entry.key ?? "").trim() === key);
  if (entity) {
    return resolveDisplayName(entity.name, entity.key) || "—";
  }
  return resolveDisplayName(key, key) || "—";
}

/**
 * Renders a `source_ref` as the `table.column` binding shown under an item's name.
 * Shared so the entity, measure, metric and dimension cards can never disagree about
 * how a partial ref (table only, column only) reads.
 */
export function formatSourceRef(
  sourceRef: Record<string, unknown> | undefined,
): string {
  if (!sourceRef) {
    return "";
  }
  const table = typeof sourceRef.table === "string" ? sourceRef.table.trim() : "";
  const column = typeof sourceRef.column === "string" ? sourceRef.column.trim() : "";
  if (table && column) {
    return `${table}.${column}`;
  }
  return table || column;
}
